class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    // Removed the focusout event listener
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    // Add a new event listener to handle when the search modal is opened
    const searchModal = document.querySelector(".search-modal .modal__content");
    if (searchModal) {
      searchModal.addEventListener(
        "classChange",
        this.onModalClassChange.bind(this)
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[limit]=15&resources[limit_scope]=each&resources[type]=product,query,article,collection&resources[options][fields]=title,variants.sku`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const predictiveSearchSection = doc.querySelector("#shopify-section-predictive-search");

        if (!predictiveSearchSection) {
          console.error("Predictive search section not found.");
          return this.fetchFallbackProducts(searchTerm); // Fetch fallback products if search results are not found
        }

        let productCards = Array.from(predictiveSearchSection.querySelectorAll(".product-item"));

        if (productCards.length === 0) {
          return this.fetchFallbackProducts(searchTerm); // Fetch fallback products if no matching products are found
        }

	    productCards.forEach((productCard) => {
	     if (typeof window.processProductCard === "function") {
	        window.processProductCard(productCard);
	      } else {
	        console.error("processProductCard function not found.");
	      }
	    });

        this.cachedResults[queryKey] = predictiveSearchSection.innerHTML;
        return this.checkCartAndUpdateUI(predictiveSearchSection).then(
          () => predictiveSearchSection
        );
      })
      .then((predictiveSearchSection) => {
        const updatedResultsMarkup = predictiveSearchSection
          ? predictiveSearchSection.innerHTML
          : "";
        this.renderSearchResults(updatedResultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }


  fetchFallbackProducts(searchTerm) {
    // Fetch products from a specific collection as fallback
    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const fallbackSection = doc.querySelector(
          "#shopify-section-predictive-search"
        );

        if (!fallbackSection) {
          console.error("Fallback predictive search section not found.");
          return;
        }

        const productCards = fallbackSection.querySelectorAll(".product-item");
        if (!productCards.length) {
          console.error("No fallback products found.");
          return;
        }

		productCards.forEach((productCard) => {
		          if (typeof window.processProductCard === "function") {
		            window.processProductCard(productCard);
		          } else {
		            console.error("processProductCard function not found.");
		          }
		        });

        this.cachedResults[searchTerm] = fallbackSection.innerHTML;
        return this.checkCartAndUpdateUI(fallbackSection).then(
          () => fallbackSection
        );
      })
      .then((fallbackSection) => {
        const updatedResultsMarkup = fallbackSection
          ? fallbackSection.innerHTML
          : "";
        this.renderSearchResults(updatedResultsMarkup);
      })
      .catch((error) => {
        console.error("Failed to fetch fallback products", error);
        this.close();
        throw error;
      });
  }

  async checkCartAndUpdateUI(section) {
    console.log("Check items");
    try {
      const cart = await fetch("/cart.js").then((res) => res.json());
      const productCards = section.querySelectorAll(".product-item");

      console.log(productCards);
      productCards.forEach((card) => {
        const variantId = card.getAttribute("data-variant-id");
        const cartItem = cart.items.find(
          (item) => `${item.variant_id}` === variantId
        );
        const quantityElement = card.querySelector(
          ".collection-quantity-selector"
        );
        console.log(quantityElement);
        if (cartItem) {
          const quantity = cartItem.quantity;
          console.log(quantity);
          card.querySelector(".quick-add__quantity").style.display = "flex";

          quantityElement.setAttribute("value", quantity);
          card.classList.add("in-cart");
        } else {
          card.classList.remove("in-cart");
        }
      });
    } catch (error) {
      console.error("Failed to fetch cart or update UI", error);
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    } else {
      console.error("Live region count element not found");
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    if (this.statusElement) {
      this.setLiveRegionText(this.loadingText);
      this.setAttribute("loading", true);
    } else {
      console.error("Status element not found");
    }
  }

  setLiveRegionText(statusText) {
    if (this.statusElement) {
      this.statusElement.setAttribute("aria-hidden", "false");
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute("aria-hidden", "true");
      }, 1000);
    } else {
      console.error("Status element not found");
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    } else {
      console.error("Live region count element not found");
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
  }

  // New method to handle modal class change
  onModalClassChange() {
    const searchModal = document.querySelector(".search-modal .modal__content");
    if (searchModal && searchModal.classList.contains("open_modal")) {
      if (this.getQuery().length) {
        this.open();
      }
    } else {
      this.close();
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);

function addslashes(str) {
  return (str + "").replace(/[\\"']/g, "\\$&").replace(/\u0000/g, "\\0");
}

// Observe class changes on the modal
const targetNode = document.querySelector(".search-modal .modal__content");
const config = { attributes: true, attributeFilter: ["class"] };
const callback = function (mutationsList, observer) {
  for (const mutation of mutationsList) {
    if (mutation.type === "attributes" && mutation.attributeName === "class") {
      const event = new Event("classChange");
      mutation.target.dispatchEvent(event);
    }
  }
};
const observer = new MutationObserver(callback);
if (targetNode) {
  observer.observe(targetNode, config);
}